import { useEffect, useMemo, useState } from 'react';
import useSelector from '@redux/typedHooks';
import { UrlParamsType } from '@appTypes/urlParamsType';
import { getURLParams } from '@lib/getUrlParams';

const isChecked = (urlValues: string[] | undefined, value: string) => Boolean(urlValues && urlValues.includes(value));

// Tracks the current URL filter (re-synced on filterNumber bumps) and builds a per-section
// { [optionName]: boolean } map used by CarFilter's checkboxes. Returns `null` for the map
// until the slice has filter options.
export const useFilterCheckedMap = () => {
  const { allFilters, filterNumber } = useSelector((state) => state.carFilter);
  const { chassis, categories, numberOfSeats, drivetrains, brands } = allFilters;
  const [filter, setFilter] = useState<UrlParamsType['filter']>({});

  useEffect(() => {
    setFilter(getURLParams().filter || {});
  }, [filterNumber]);

  const checkedMap = useMemo(() => {
    if (!chassis || !categories || !numberOfSeats || !drivetrains || !brands || !filter || !filterNumber) {
      return null;
    }
    const reduceByName = (opts: { name: string }[], urlValues: string[] | undefined) =>
      opts.reduce<Record<string, boolean>>((acc, cur) => {
        acc[cur.name] = isChecked(urlValues, cur.name);
        return acc;
      }, {});

    return {
      chassis: reduceByName(chassis, filter.chassis),
      brands: brands.reduce<Record<string, boolean>>((acc, cur) => {
        acc[cur.value] = isChecked(filter.brands, cur.value);
        return acc;
      }, {}),
      categories: reduceByName(categories, filter.categorynames),
      tested: isChecked(filter.tested, 'true'),
      trailerHitch: isChecked(filter.trailerHitch, 'true'),
      numberOfSeats: reduceByName(numberOfSeats, filter.numberOfSeats),
      drivetrains: reduceByName(drivetrains, filter.drivetrains),
    };
  }, [filter, chassis, categories, drivetrains, numberOfSeats, brands, filterNumber]);

  return { filter, checkedMap };
};
